import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Copy, Check, LayoutDashboard, DollarSign, PauseCircle, CheckCircle,
  Trash2, Folder, FolderOpen, ChevronRight, Repeat, Loader2, User, Building2,
} from 'lucide-react';

const STATUS_PILL = {
  active: { cls: 'bg-emerald-600 text-white', label: 'Active' },
  low_activity: { cls: 'bg-yellow-500 text-white', label: 'Low Activity' },
  paused: { cls: 'bg-amber-600 text-white', label: 'Paused' },
  inactive: { cls: 'bg-amber-600 text-white', label: 'Paused' },
  archived: { cls: 'bg-red-600 text-white', label: 'Archived' },
};

const maskId = (id) => (id && id.length > 14 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id || '');

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-500 mb-2">{children}</p>
);

const inputCls = 'w-full px-3 py-2.5 rounded-lg text-sm bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-teal-500 dark:focus:border-teal-400/50 transition-colors';

export const SubAccountDrawer = ({
  account, workspaceId, api, folders, atLimit,
  onClose, onChanged, onEditPricing, onUpdateStatus, onArchive, onRestore, onMoveToFolder, onUpgrade,
}) => {
  const [name, setName] = useState(account.name || '');
  const [contact, setContact] = useState({
    contact_name: account.contact_name || '',
    contact_email: account.contact_email || '',
    contact_phone: account.contact_phone || '',
  });
  const [rebillingOn, setRebillingOn] = useState(!!account.rebilling_enabled);
  const [rebillingRate, setRebillingRate] = useState(account.rebilling_rate || account.rate_per_min || 0.15);
  const [savingName, setSavingName] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingRate, setSavingRate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);

  const isArchived = account.status === 'archived';
  const pill = STATUS_PILL[account.status] || STATUS_PILL.paused;

  const copyId = () => {
    navigator.clipboard.writeText(account.sub_account_id);
    setCopied(true);
    toast.success('Sub-account ID copied');
    setTimeout(() => setCopied(false), 1500);
  };

  const saveName = async () => {
    if (!name.trim()) { toast.error('Name cannot be empty'); return; }
    setSavingName(true);
    try {
      await api.put(`/api/sub-accounts/${account.sub_account_id}`, { name: name.trim() });
      toast.success('Business name updated');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update name'); }
    setSavingName(false);
  };

  const saveContact = async () => {
    setSavingContact(true);
    try {
      await api.put(`/api/sub-accounts/${account.sub_account_id}`, { ...contact });
      toast.success('Client information updated');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update client info'); }
    setSavingContact(false);
  };

  const toggleRebilling = async () => {
    const next = !rebillingOn;
    setRebillingOn(next);
    try {
      await api.put(`/api/sub-accounts/${account.sub_account_id}`, { rebilling_enabled: next });
      toast.success(next ? 'Re-billing enabled' : 'Re-billing disabled');
      onChanged();
    } catch (e) {
      setRebillingOn(!next);
      toast.error(e.response?.data?.detail || 'Failed to update re-billing');
    }
  };

  const saveRebillingRate = async () => {
    setSavingRate(true);
    try {
      await api.put(`/api/sub-accounts/${account.sub_account_id}`, { rebilling_rate: parseFloat(rebillingRate) });
      toast.success('Re-billing rate updated');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update rate'); }
    setSavingRate(false);
  };

  const ActionRow = ({ icon: Icon, label, sub, onClick, tone = 'default', right = null, testid }) => {
    const tones = {
      default: 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06]',
      teal: 'text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-400/10',
      green: 'text-emerald-700 dark:text-green-400 hover:bg-emerald-50 dark:hover:bg-green-400/10',
      amber: 'text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-400/10',
      red: 'text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-400/10',
    };
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-xl transition-colors ${tones[tone]}`}
        data-testid={testid}
      >
        <span className="flex items-center gap-3">
          <Icon size={16} />
          <span className="text-left">
            <span className="font-medium">{label}</span>
            {sub && <p className="text-[10px] text-gray-500 font-normal">{sub}</p>}
          </span>
        </span>
        {right}
      </button>
    );
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="subaccount-drawer-overlay">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        transition={{ type: 'tween', duration: 0.25, ease: 'easeOut' }}
        className="absolute right-0 top-0 h-full w-full max-w-[440px] xl:w-[25vw] xl:min-w-[440px] bg-white dark:bg-[#0c0d12] border-l border-gray-200 dark:border-white/[0.08] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="subaccount-drawer"
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 dark:border-white/[0.06] shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate" data-testid="drawer-business-name">{account.name}</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[11px] font-mono text-gray-500" data-testid="drawer-subaccount-id">ID: {maskId(account.sub_account_id)}</span>
                <button onClick={copyId} className="p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" data-testid="drawer-copy-id">
                  {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${pill.cls}`} data-testid="drawer-status-pill">
                {pill.label}
              </span>
              <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors" data-testid="drawer-close">
                <X size={18} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-7">
          {/* Quick Actions */}
          <div>
            <SectionLabel>Quick Actions</SectionLabel>
            <div className="space-y-0.5">
              {!isArchived && (
                <ActionRow
                  icon={LayoutDashboard} label="Open Dashboard" tone="teal"
                  onClick={() => window.open(`/workspace/${workspaceId}/sub-account/${account.sub_account_id}`, '_blank')}
                  right={<ChevronRight size={14} className="text-gray-400" />}
                  testid="action-open-dashboard"
                />
              )}

              {/* Re-billing toggle — its own save, completely separate */}
              {!isArchived && (
                <div className="px-3 py-2.5 rounded-xl" data-testid="drawer-rebilling-block">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                      <Repeat size={16} />
                      <span>
                        Client Re-billing
                        <p className="text-[10px] text-gray-500 font-normal">Charge your client a markup on usage</p>
                      </span>
                    </span>
                    <button
                      onClick={toggleRebilling}
                      className={`w-11 h-6 rounded-full transition-colors shrink-0 ${rebillingOn ? 'bg-emerald-600' : 'bg-gray-300 dark:bg-white/20'}`}
                      data-testid="drawer-rebilling-toggle"
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${rebillingOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {rebillingOn && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="relative flex-1">
                        <DollarSign size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="number" step="0.01" min="0" value={rebillingRate}
                          onChange={(e) => setRebillingRate(e.target.value)}
                          className={`${inputCls} pl-7 py-2`}
                          data-testid="drawer-rebilling-rate-input"
                        />
                      </div>
                      <span className="text-xs text-gray-500">/min</span>
                      <button
                        onClick={saveRebillingRate}
                        disabled={savingRate}
                        className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                        data-testid="drawer-save-rate"
                      >
                        {savingRate ? <Loader2 size={12} className="animate-spin" /> : 'Save Rate'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {!isArchived && (
                <ActionRow
                  icon={DollarSign} label="Edit Pricing" sub="Upfront fee & monthly recurring"
                  onClick={() => { onClose(); onEditPricing(account); }}
                  testid="action-edit-pricing"
                />
              )}

              {!isArchived && (
                <>
                  <ActionRow
                    icon={FolderOpen} label="Move to folder"
                    onClick={() => setShowMoveMenu(!showMoveMenu)}
                    right={<ChevronRight size={14} className={`text-gray-400 transition-transform ${showMoveMenu ? 'rotate-90' : ''}`} />}
                    testid="action-move-folder"
                  />
                  {showMoveMenu && (
                    <div className="mx-1 my-1 p-1 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06]" data-testid="move-folder-submenu">
                      <button
                        onClick={() => { onMoveToFolder(account.sub_account_id, null); setShowMoveMenu(false); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg"
                        data-testid="move-folder-unfiled"
                      >
                        <Folder size={14} /> Unfiled
                      </button>
                      {folders.length === 0 && <p className="px-3 py-2 text-[11px] text-gray-500">No folders yet — create one first.</p>}
                      {folders.map((f) => (
                        <button
                          key={f.folder_id}
                          onClick={() => { onMoveToFolder(account.sub_account_id, f.folder_id); setShowMoveMenu(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-lg"
                          data-testid={`move-to-folder-${f.folder_id}`}
                        >
                          <Folder size={14} style={{ color: f.color }} />
                          {f.name}
                          {account.folder_id === f.folder_id && <Check size={12} className="ml-auto text-emerald-500" />}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}

              {!isArchived && account.status !== 'active' && (
                <ActionRow icon={CheckCircle} label="Activate" tone="green" onClick={() => onUpdateStatus(account.sub_account_id, 'active')} testid="action-activate" />
              )}
              {!isArchived && account.status !== 'paused' && account.status !== 'inactive' && (
                <ActionRow icon={PauseCircle} label="Pause" sub="Pauses activity, keeps slot" tone="amber" onClick={() => onUpdateStatus(account.sub_account_id, 'paused')} testid="action-pause" />
              )}
              {isArchived ? (
                <ActionRow
                  icon={CheckCircle} label="Restore" tone={atLimit ? 'default' : 'green'}
                  sub={atLimit ? 'Limit reached — upgrade to restore' : undefined}
                  onClick={() => { onClose(); atLimit ? onUpgrade() : onRestore(account.sub_account_id); }}
                  testid="action-restore"
                />
              ) : (
                <ActionRow icon={Trash2} label="Archive" sub="Removes from active, frees a slot" tone="red" onClick={() => onArchive(account.sub_account_id)} testid="action-archive" />
              )}
            </div>
          </div>

          {/* Account Information — name saves on its own */}
          <div>
            <SectionLabel>Account Information</SectionLabel>
            <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1.5"><Building2 size={12} /> Business Name</label>
            <div className="flex items-center gap-2">
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                className={inputCls} placeholder="Business name"
                data-testid="drawer-name-input"
              />
              <button
                onClick={saveName}
                disabled={savingName || name.trim() === (account.name || '')}
                className="px-4 py-2.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold transition-colors disabled:opacity-40 shrink-0"
                data-testid="drawer-save-name"
              >
                {savingName ? <Loader2 size={14} className="animate-spin" /> : 'Save'}
              </button>
            </div>
          </div>

          {/* Client Information */}
          <div>
            <SectionLabel>Client Information</SectionLabel>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1.5 flex items-center gap-1.5"><User size={12} /> Full Name</label>
                <input
                  type="text" value={contact.contact_name}
                  onChange={(e) => setContact({ ...contact, contact_name: e.target.value })}
                  className={inputCls} placeholder="e.g. John Smith"
                  data-testid="drawer-contact-name"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Email</label>
                <input
                  type="email" value={contact.contact_email}
                  onChange={(e) => setContact({ ...contact, contact_email: e.target.value })}
                  className={inputCls} placeholder="client@business.com"
                  data-testid="drawer-contact-email"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">Phone Number</label>
                <input
                  type="tel" value={contact.contact_phone}
                  onChange={(e) => setContact({ ...contact, contact_phone: e.target.value })}
                  className={inputCls} placeholder="+1 555 000 0000"
                  data-testid="drawer-contact-phone"
                />
              </div>
              <button
                onClick={saveContact}
                disabled={savingContact}
                className="w-full px-4 py-2.5 rounded-lg bg-gray-900 dark:bg-white/10 hover:bg-gray-800 dark:hover:bg-white/15 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                data-testid="drawer-save-contact"
              >
                {savingContact ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'Save Client Information'}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubAccountDrawer;
